import { Injectable } from '@angular/core';
import { ApiNotificationsService, NotificationParameterFlags, TypeNotification } from '@timechef/core/api';
import { AuthRepository } from '@timechef/core/auth';
import { AppRepository } from '@timechef/core/state';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { NotificationStatusKey, NotificationsRepository, buildUpdateSettingsKey } from '.';

const NOTIFICATIONS_PAGE_SIZE = 10;

@Injectable({
  providedIn: 'root',
})
export class NotificationsService {
  constructor(
    private notificationsRepository: NotificationsRepository,
    private authRepository: AuthRepository,
    private api: ApiNotificationsService,
    private appRepo: AppRepository,
  ) {}

  getNotifications(pageIndex: number) {
    return this.api.getNotifications({ PageIndex: pageIndex, PageSize: NOTIFICATIONS_PAGE_SIZE }).pipe(
      this.notificationsRepository.notificationsByPeriodDS.trackRequestStatus(),
      tap((data) => {
        this.notificationsRepository.addNotifications(data.notifications, data.hasMore, pageIndex);
      }),
    );
  }

  getNotificationSettingByFlag(flag: NotificationParameterFlags, type: TypeNotification): Observable<boolean> {
    return of(this.notificationsRepository.getNotificationSettings()).pipe(
      switchMap((settings) => {
        if (!settings || settings.length === 0) {
          return this.getNotificationSettings().pipe(map((x) => x.notificationSettings));
        }

        return of(settings);
      }),
      map((settings) => {
        const setting = settings.find((x) => x.category === flag);
        if (!setting) return false;

        switch (type) {
          case TypeNotification.Email:
            return setting.emailEnabled;
          case TypeNotification.Push:
            return setting.pushEnabled;
          case TypeNotification.Sms:
            return setting?.smsEnabled;
          default:
            return false;
        }
      }),
    );
  }

  getNotificationSettings() {
    return this.api.getSettings().pipe(
      this.notificationsRepository.notificationSettingsDS.trackRequestStatus(),
      tap((data) => {
        this.notificationsRepository.updateNotificationSettings(data.notificationSettings);
      }),
    );
  }

  updateNotificationSetting(category: NotificationParameterFlags, type: TypeNotification, enable: boolean) {
    return this.api.postSettings({ body: { category, type, enable } }).pipe(
      this.notificationsRepository.trackRequestStatus(NotificationStatusKey.UpdateSettings),
      this.notificationsRepository.trackRequestStatus(buildUpdateSettingsKey(category, type)),
      tap(() => {
        this.notificationsRepository.updateNotificationSetting(category, type, enable);
      }),
    );
  }

  updateOptInSettings(acceptEliorOffers?: boolean, acceptPartnersOffers?: boolean) {
    return this.api.postOptInSettings({ body: { acceptEliorOffers, acceptPartnersOffers } }).pipe(
      this.authRepository.userOptInSettingsDS.trackRequestStatus(),
      tap(() => {
        this.authRepository.updateOptInSettings(acceptEliorOffers, acceptPartnersOffers);
      }),
    );
  }

  deleteNotification(notificationId: number) {
    //we first remove the entitie from the store in order to delete the notification immediatly on screen
    //deletion on api is done after
    this.notificationsRepository.deleteNotification(notificationId);
    return this.api.deleteNotification({ body: { notificationId } });
  }

  markNotificationsAsRead(ids: number[]) {
    //we update first in the repo to mark the notification as read immediatly on screen
    this.notificationsRepository.markNotificationsAsRead(ids);
    //we update as well unread notification count, used in navbar
    this.appRepo.setNotificationsCount(Math.max(0, this.appRepo.getNotificationsCount() - ids.length));
    return this.api.postSetAsRead({ body: { notificationIds: ids } });
  }
}
